/**
 * Feature 2C — governed maintenance draft proposal (locator from healing evidence).
 * Draft-only: never mutates published canvas_json; never auto-publishes.
 */

import { z } from 'zod';

export const MAINTENANCE_DRAFT_CHANGE_TYPES = ['locator', 'assertion', 'api_contract', 'manual_review'] as const;
export type MaintenanceDraftChangeType = (typeof MAINTENANCE_DRAFT_CHANGE_TYPES)[number];

export const MAINTENANCE_DRAFT_STATUSES = [
  'proposed',
  'insufficient_evidence',
  'stale',
  'duplicate',
  'rejected_preview',
  /** Feature 2D — multiple similarly plausible healing candidates; human must pick stepIndex. */
  'ambiguous',
  /** Feature 2D — failure class / flake indicates locator rewrite would be a false positive. */
  'no_healing',
] as const;
export type MaintenanceDraftStatus = (typeof MAINTENANCE_DRAFT_STATUSES)[number];

export const ProposeMaintenanceDraftRequestSchema = z
  .object({
    /** Required to write the draft. Preview/dry-run may omit or set false. */
    confirm: z.boolean().optional().default(false),
    /** When true, compute before/after without writing draft_canvas_json. */
    dryRun: z.boolean().optional().default(false),
    sourceRunId: z.string().uuid(),
    stepIndex: z.number().int().nonnegative().optional(),
    changeReviewId: z.string().uuid().optional(),
  })
  .refine((b) => b.dryRun === true || b.confirm === true, {
    message: 'confirm:true is required to write a draft (or set dryRun:true for preview)',
  });
export type ProposeMaintenanceDraftRequest = z.infer<typeof ProposeMaintenanceDraftRequestSchema>;

export const MaintenanceDraftProposalSchema = z.object({
  flowId: z.string().uuid(),
  flowName: z.string().nullable(),
  projectId: z.string().uuid(),
  sourceRunId: z.string().uuid(),
  changeReviewId: z.string().uuid().nullable().optional(),
  changeType: z.enum(MAINTENANCE_DRAFT_CHANGE_TYPES),
  status: z.enum(MAINTENANCE_DRAFT_STATUSES),
  stepId: z.string().nullable(),
  stepIndex: z.number().int().nonnegative().nullable(),
  stepTitle: z.string().nullable(),
  before: z.string().nullable(),
  proposedAfter: z.string().nullable(),
  evidence: z.array(z.string().max(500)).max(20),
  confidence: z.enum(['low', 'medium', 'high', 'insufficient']),
  reason: z.string().max(2_000),
  strategy: z.string().nullable().optional(),
  /** Feature 2D — composed recommendation kind when applicable. */
  healingRecommendation: z
    .enum(['propose_draft', 'ambiguous', 'no_healing', 'insufficient', 'review_required'])
    .optional(),
  /** Hard proof for governance tests. */
  publishedCanvasUnchanged: z.literal(true),
  draftWritten: z.boolean(),
  draftVersion: z.number().int().nonnegative().nullable().optional(),
  draftHref: z.string(),
  reviewsHref: z.string(),
  proposedBy: z.string().uuid().nullable().optional(),
  proposedAt: z.string().nullable().optional(),
});
export type MaintenanceDraftProposal = z.infer<typeof MaintenanceDraftProposalSchema>;

export const ProposeMaintenanceDraftResponseSchema = z.object({
  proposal: MaintenanceDraftProposalSchema,
});
export type ProposeMaintenanceDraftResponse = z.infer<typeof ProposeMaintenanceDraftResponseSchema>;

/** Advisory hint on Change Impact — does not write anything. */
export const MaintenanceDraftHintSchema = z.object({
  flowId: z.string().uuid(),
  flowName: z.string().nullable().optional(),
  sourceRunId: z.string().uuid(),
  candidateCount: z.number().int().nonnegative(),
  sampleBefore: z.string().nullable().optional(),
  sampleAfter: z.string().nullable().optional(),
  confidence: z.enum(['low', 'medium', 'high', 'insufficient']).optional(),
  /** Feature 2D — composed healing recommendation for this hint's source run. */
  healingRecommendation: z
    .enum(['propose_draft', 'ambiguous', 'no_healing', 'insufficient', 'review_required'])
    .optional(),
  why: z.array(z.string().max(300)).max(8).optional(),
  /** Feature 3B+3C — advisory note from historical resolution (never auto-writes). */
  historicalResolutionNote: z.string().max(400).optional(),
  historicalMatchStrength: z.enum(['none', 'limited', 'possible', 'strong', 'verified']).optional(),
});
export type MaintenanceDraftHint = z.infer<typeof MaintenanceDraftHintSchema>;
